import { ValidationError } from '../../../shared/exceptions/validation.error';
import { BaseService } from '../../base';
import type {
  AttachQuotePayload,
  CalculateQuotePayload,
  CalculateQuoteResponse,
  CreateQuotePayload,
  CreateQuoteResponse,
  GetInspectionDocumentResponse,
  GetPersonDataPayload,
  GetPersonDataResponse,
  GetProposalPdfPayload,
  GetProposalPdfResponse,
} from '../types';

const ADDITIONAL_COVERAGE_LIMIT_REGEX =
  /(deve ser de at[eé]\s+)(\d+(?:[.,]\d+)?)(\s+da cobertura b[aá]sica)/i;
const ELECTRICAL_DAMAGE_LIMIT_EXCEEDED_REGEX =
  /limite de import[âa]ncia segurada.*danos el[ée]tricos.*foi excedido/i;
const ELECTRICAL_DAMAGE_PERCENT_LIMIT = 10;

function formatAdditionalCoverageLimitMessage(message: string): string {
  if (ELECTRICAL_DAMAGE_LIMIT_EXCEEDED_REGEX.test(message)) {
    return `O valor da IS da cobertura Danos Elétricos deve ser de até ${ELECTRICAL_DAMAGE_PERCENT_LIMIT}% da cobertura básica.`;
  }

  const match = message.match(ADDITIONAL_COVERAGE_LIMIT_REGEX);

  if (!match) return message;

  const [, prefix, rawValue, suffix] = match;
  const parsedValue = Number(rawValue.replace(',', '.'));

  if (!Number.isFinite(parsedValue)) return message;

  const percentageValue = parsedValue <= 1 ? parsedValue * 100 : parsedValue;
  const formattedPercentage = Number.isInteger(percentageValue)
    ? `${percentageValue.toFixed(0)}%`
    : `${percentageValue.toFixed(2).replace(/\.?0+$/, '')}%`;

  return message.replace(
    ADDITIONAL_COVERAGE_LIMIT_REGEX,
    `${prefix}${formattedPercentage}${suffix}`
  );
}

const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

export class HomeownerQuotesService extends BaseService {
  constructor() {
    super();
  }

  async createQuote(payload: CreateQuotePayload): Promise<{
    success: boolean;
    data?: CreateQuoteResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = '/portal/v1/homeowner/quotes';
      const { previousInsurer, ...rest } = payload;
      const response = await this.post(
        path,
        {
          ...rest,
          ...(previousInsurer ? { seguradoraAnterior: previousInsurer } : {}),
        },
        PORTAL_API_URL
      );
      return await this.handleJsonResponse<CreateQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro ao criar cotação',
      };
    }
  }

  async updateQuote(
    id: string,
    payload: Partial<AttachQuotePayload>
  ): Promise<{
    success: boolean;
    data?: CreateQuotePayload;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/homeowner/quotes/${id}/attach-element`;
      const { previousInsurer, ...rest } = payload;
      const response = await this.post(
        path,
        {
          ...rest,
          ...(previousInsurer ? { seguradoraAnterior: previousInsurer } : {}),
        },
        PORTAL_API_URL
      );

      if (response.status === 422) {
        const data = (await response.json()) as {
          detail?: string;
          errors: Record<string, { code: string; message: string }>;
        };

        const normalizedFieldErrors = Object.fromEntries(
          Object.entries(data.errors ?? {}).map(([key, value]) => [
            key,
            {
              ...value,
              message: formatAdditionalCoverageLimitMessage(
                String(value?.message ?? '').trim()
              ),
            },
          ])
        ) as Record<string, { code: string; message: string }>;

        const messages =
          Object.values(normalizedFieldErrors)
            ?.map(error => error.message)
            .filter(Boolean)
            .map(message => message.trim()) ?? [];

        const validationError = new ValidationError(messages);
        const rawDetail = String(data.detail ?? '').trim();
        const detailFromResponse =
          rawDetail &&
          rawDetail !== 'Validation error' &&
          rawDetail !== 'A proposta foi rejeitada pela validação'
            ? rawDetail
            : undefined;

        if (detailFromResponse) {
          validationError.detail = detailFromResponse;
        } else if (
          messages.length > 0 &&
          messages.every(m => m.includes(' - '))
        ) {
          const prefixes = messages.map(m => m.split(' - ')[0].trim());
          if (prefixes.every(p => p === prefixes[0])) {
            validationError.detail = prefixes[0];
            validationError.messages = messages.map(m =>
              m.split(' - ').slice(1).join(' - ').trim()
            );
          }
        } else if (rawDetail && messages.length > 0) {
          validationError.detail = rawDetail;
        }

        validationError.fieldErrors = normalizedFieldErrors;
        throw validationError;
      }

      return await this.handleJsonResponse<CreateQuotePayload>(response);
    } catch (error) {
      if (error instanceof ValidationError) throw error;

      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao atualizar cotação',
      };
    }
  }

  async calculateQuote(payload: CalculateQuotePayload): Promise<{
    success: boolean;
    data?: CalculateQuoteResponse;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/homeowner/quotes/${payload.policyId}/calculate`;
      const response = await this.post(path, {}, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<CalculateQuoteResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao calcular cotação',
      };
    }
  }

  async getInspectionDocument(policyId: string): Promise<{
    success: boolean;
    data?: GetInspectionDocumentResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/homeowner/quotes/${encodeURIComponent(policyId)}/documents`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<GetInspectionDocumentResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao carregar documento de inspeção',
      };
    }
  }

  async getProposalPdf(payload: GetProposalPdfPayload): Promise<{
    success: boolean;
    data?: GetProposalPdfResponse;
    error?: string;
  }> {
    try {
      const { policyId, productCode, stage } = payload;
      const path = `/portal/v1/quotes/${policyId}/pdf`;
      const normalizedPayload = {
        productCode: productCode || '14001',
        stage: stage || 'quote',
      };
      const response = await this.post(
        path,
        normalizedPayload,
        {},
        PORTAL_API_URL
      );

      if (!response.ok) {
        const text = (await response.text()).trim();
        let apiMessage: string | undefined;

        if (text) {
          try {
            const data = JSON.parse(text) as Record<string, unknown>;
            const problemDetail =
              typeof data.detail === 'string' && data.detail;
            const direct =
              (typeof data.message === 'string' && data.message) ||
              (typeof data.error === 'string' && data.error);
            const problemTitle = typeof data.title === 'string' && data.title;
            const errors = data.errors;

            if (problemDetail) apiMessage = problemDetail;
            else if (direct) apiMessage = direct;
            else if (problemTitle) apiMessage = problemTitle;
            else if (Array.isArray(errors) && errors.length > 0) {
              const first = errors[0] as unknown;
              if (typeof first === 'string') apiMessage = first;
              else if (first && typeof first === 'object') {
                const e = first as Record<string, unknown>;
                const msg = typeof e.message === 'string' && e.message;
                if (msg) apiMessage = msg;
              }
            }
          } catch {
            apiMessage = text;
          }
        }

        // PDF não passa pelo `handleJsonResponse`: avisa corretora inativa aqui.
        this.notifyInactiveBroker(response.status, apiMessage);

        return {
          success: false,
          error:
            apiMessage || `Erro ${response.status}: ${response.statusText}`,
        };
      }

      const data =
        await this.handleJsonResponse<GetProposalPdfResponse>(response);

      if (!data.success || !data.data) {
        return {
          success: false,
          error: data.error || 'Erro ao gerar URL do PDF',
        };
      }

      return { success: true, data: data.data };
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao gerar PDF da proposta',
      };
    }
  }

  async getPersonData(payload: GetPersonDataPayload): Promise<{
    success: boolean;
    data?: GetPersonDataResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/quotes/lookup?search=${encodeURIComponent(payload.registerNumber)}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<GetPersonDataResponse>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error
            ? error.message
            : 'Erro ao buscar dados da pessoa',
      };
    }
  }

  async validateInspectionJourney(policyId: string): Promise<{
    success: boolean;
    error?: string;
    message?: string;
  }> {
    try {
      const path = `/portal/v1/homeowner/quotes/${encodeURIComponent(policyId)}/validate?code=validation_inspection_journey`;
      const response = await this.post(path, {}, PORTAL_API_URL);
      return await this.handleJsonResponse<void>(response);
    } catch (error) {
      return {
        success: false,
        error:
          error instanceof Error ? error.message : 'Erro ao validar inspeção',
      };
    }
  }
}

export const homeownerQuotesService = new HomeownerQuotesService();
